/**
 * Hero Banner Carousel (Full-Bleed)
 *
 * Full-width image carousel with Swiper. Pairs with sections/hero-banner-carousel.liquid.
 *
 * Performance & correctness contract:
 *  - Single Swiper instance per section; destroyed cleanly on theme editor unload
 *  - Autoplay stops when: tab hidden, section offscreen, user clicks pause button
 *  - GTM tracking via event delegation (survives Swiper loop-mode clones)
 *  - Keyboard navigation enabled, respects prefers-reduced-motion
 *  - Zero dependencies beyond global Swiper
 *
 * @requires Swiper (global, loaded in layout/theme.liquid)
 */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 750;
  var INTERSECTION_THRESHOLD = 0.25;
  var RESIZE_DEBOUNCE_MS = 200;

  function parseBool(value, fallback) {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }

  function parseInt10(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function HeroBannerCarousel(container) {
    this.container = container;
    this.swiper = null;
    this.destroyed = false;
    this.userPaused = false;

    this.swiperEl = container.querySelector('.hero-banner-carousel__swiper');
    if (!this.swiperEl) return;

    this.prevEl = container.querySelector('.hero-banner-carousel__arrow--prev');
    this.nextEl = container.querySelector('.hero-banner-carousel__arrow--next');
    this.paginationEl = container.querySelector('.hero-banner-carousel__pagination');
    this.slides = container.querySelectorAll('.hero-banner-carousel__slide');

    var d = container.dataset;
    this.config = {
      autoplay: parseBool(d.autoplay, true),
      autoplaySpeed: parseInt10(d.autoplaySpeed, 5000),
      transitionSpeed: parseInt10(d.transitionSpeed, 500),
      pauseOnHover: parseBool(d.pauseOnHover, true)
    };

    // Defer Swiper init off the LCP critical path. Slide 1 is already painted
    // as plain HTML (CSS hides slides 2+ until .swiper-initialized). Initializing
    // Swiper synchronously here adds ~100–300ms of layout/transform work that
    // delays the LCP paint event. requestIdleCallback (fallback: rAF + setTimeout)
    // schedules init after the LCP frame is committed.
    var self = this;
    var schedule = window.requestIdleCallback
      ? function (fn) { window.requestIdleCallback(fn, { timeout: 1500 }); }
      : function (fn) { setTimeout(fn, 1); };

    // Wire up GTM click tracking synchronously so early clicks (pre-Swiper) are captured.
    this.setupTracking();

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      // Theme editor expects immediate init for block:select to work.
      this.init();
    } else {
      schedule(function () { if (!self.destroyed) self.init(); });
    }
  }

  HeroBannerCarousel.prototype.init = function () {
    if (typeof Swiper === 'undefined' || !this.swiperEl) return;

    if (this.slides.length <= 1) {
      this.container.classList.add('hero-banner-carousel--single');
      return;
    }

    var reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    var swiperOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      speed: reducedMotion ? 0 : this.config.transitionSpeed,
      grabCursor: true,
      watchSlidesProgress: false,
      preventInteractionOnTransition: false,

      autoplay:
        this.config.autoplay && !reducedMotion
          ? {
              delay: this.config.autoplaySpeed,
              disableOnInteraction: false,
              pauseOnMouseEnter: this.config.pauseOnHover && !isMobile,
              waitForTransition: true
            }
          : false,

      keyboard: {
        enabled: true,
        onlyInViewport: true
      },

      a11y: {
        prevSlideMessage: 'Previous banner',
        nextSlideMessage: 'Next banner',
        paginationBulletMessage: 'Go to banner {{index}}',
        slideLabelMessage: ''
      }
    };

    if (this.prevEl && this.nextEl) {
      swiperOptions.navigation = {
        prevEl: this.prevEl,
        nextEl: this.nextEl
      };
    }

    if (this.paginationEl) {
      swiperOptions.pagination = {
        el: this.paginationEl,
        clickable: true,
        bulletElement: 'button'
      };
    }

    this.swiper = new Swiper(this.swiperEl, swiperOptions);

    this.setupLifecycleListeners();
  };

  HeroBannerCarousel.prototype.setupTracking = function () {
    var self = this;
    // Event delegation on the container — handles Swiper loop clones without re-binding.
    this.clickHandler = function (e) {
      // Slide click (carousel banner)
      var slide = e.target.closest('.hero-banner-carousel__slide[data-slide-index]');
      if (slide && slide.getAttribute('href')) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'hero_banner_click',
          slide_index: parseInt10(slide.dataset.slideIndex, 0),
          slide_alt: slide.dataset.slideAlt || '',
          click_url: slide.getAttribute('href'),
          section_id: self.container.dataset.sectionId
        });
        return;
      }
      // Strip click (single shared image below banner)
      var strip = e.target.closest('.hero-banner-carousel__strip[data-strip-link]');
      if (strip && strip.getAttribute('href')) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'hero_banner_strip_click',
          strip_alt: strip.getAttribute('aria-label') || '',
          click_url: strip.getAttribute('href'),
          section_id: self.container.dataset.sectionId
        });
      }
    };
    this.container.addEventListener('click', this.clickHandler);
  };

  HeroBannerCarousel.prototype.setupLifecycleListeners = function () {
    var self = this;

    this.visibilityHandler = function () {
      if (!self.swiper || !self.swiper.autoplay || !self.config.autoplay || self.userPaused) return;
      if (document.hidden) {
        self.swiper.autoplay.stop();
      } else if (self.isInViewport) {
        self.swiper.autoplay.start();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.isInViewport = true;
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        function (entries) {
          if (!self.swiper || !self.swiper.autoplay || !self.config.autoplay) return;
          self.isInViewport = entries[0].isIntersecting;
          if (self.userPaused) return;
          if (self.isInViewport) {
            self.swiper.autoplay.start();
          } else {
            self.swiper.autoplay.stop();
          }
        },
        { threshold: INTERSECTION_THRESHOLD }
      );
      this.observer.observe(this.container);
    }

    var resizeTimer = null;
    this.resizeHandler = function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (!self.destroyed && self.swiper) self.swiper.update();
      }, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener('resize', this.resizeHandler, { passive: true });
  };

  HeroBannerCarousel.prototype.goToBlock = function (blockId) {
    if (!this.swiper) return;
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].dataset.blockId === blockId) {
        this.swiper.slideToLoop(i);
        if (this.swiper.autoplay) this.swiper.autoplay.stop();
        return;
      }
    }
  };

  HeroBannerCarousel.prototype.destroy = function () {
    this.destroyed = true;

    if (this.clickHandler) this.container.removeEventListener('click', this.clickHandler);
    if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    if (this.observer) this.observer.disconnect();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  };

  // -------- Manager --------
  var instances = {};

  function getId(container) {
    return container.dataset.sectionId || container.id;
  }

  function create(container) {
    var id = getId(container);
    if (instances[id]) instances[id].destroy();
    instances[id] = new HeroBannerCarousel(container);
  }

  function destroy(id) {
    if (instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }
  }

  function initAll() {
    var containers = document.querySelectorAll('.hero-banner-carousel');
    for (var i = 0; i < containers.length; i++) create(containers[i]);
  }

  // Theme editor lifecycle — only bind if in design mode to save main-thread work on live storefront.
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var el = e.target.querySelector('.hero-banner-carousel');
      if (el) create(el);
    });

    document.addEventListener('shopify:section:unload', function (e) {
      var el = e.target.querySelector('.hero-banner-carousel');
      if (el) destroy(getId(el));
    });

    document.addEventListener('shopify:section:select', function (e) {
      var el = e.target.querySelector('.hero-banner-carousel');
      if (!el) return;
      var instance = instances[getId(el)];
      if (instance && instance.swiper && instance.swiper.autoplay) instance.swiper.autoplay.stop();
    });

    document.addEventListener('shopify:section:deselect', function (e) {
      var el = e.target.querySelector('.hero-banner-carousel');
      if (!el) return;
      var instance = instances[getId(el)];
      if (
        instance &&
        instance.swiper &&
        instance.swiper.autoplay &&
        instance.config.autoplay &&
        !instance.userPaused
      ) {
        instance.swiper.autoplay.start();
      }
    });

    document.addEventListener('shopify:block:select', function (e) {
      var section = e.target.closest('.hero-banner-carousel');
      if (!section) return;
      var instance = instances[getId(section)];
      if (instance) instance.goToBlock(e.detail.blockId);
    });

    document.addEventListener('shopify:block:deselect', function (e) {
      var section = e.target.closest('.hero-banner-carousel');
      if (!section) return;
      var instance = instances[getId(section)];
      if (
        instance &&
        instance.swiper &&
        instance.swiper.autoplay &&
        instance.config.autoplay &&
        !instance.userPaused
      ) {
        instance.swiper.autoplay.start();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Expose for debugging / programmatic control
  window.HeroBannerCarousel = HeroBannerCarousel;
})();
